import * as React from "react";
import { DroppsProduct, variantSelect } from "@/store";
import {
  cancelOnetime,
  cancelSubscription,
  updateAddress,
  updateNextChargeDate,
  updateOneTimeProduct,
  updateSubscription,
  processCharge,
  fetchSubscriptions,
  skipCharge,
  skipSubscription,
  updateBulkSubscriptions,
} from "./RechargeApiActions";
import {
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { handleError } from "@/helpers/errors";
import { Cadence, RechargeItemRaw, BoxItem } from "./types";
import { track } from "@/helpers/googleAnalytics";

const SelectedItemContext = React.createContext<
  { boxItem: BoxItem } | undefined
>(undefined);

type SelectedItemProviderProps = {
  boxItem: BoxItem;
  children: React.ReactNode;
};
const SelectedItemProvider = ({
  children,
  boxItem,
}: SelectedItemProviderProps) => {
  const value = { boxItem };
  return (
    <SelectedItemContext.Provider value={value}>
      {children}
    </SelectedItemContext.Provider>
  );
};

export const updateSubscriptionQueryData = (
  queryClient: QueryClient,
  data: RechargeItemRaw | RechargeItemRaw[]
) => {
  if (Array.isArray(data)) {
    queryClient.setQueryData<RechargeItemRaw[]>(
      ["rechargeSubscriptions"],
      (oldData) =>
        oldData?.map((sub) => {
          const matchingItem = data.find((item) => item.id === sub.id);
          return matchingItem || sub;
        })
    );
  } else {
    queryClient.setQueryData<RechargeItemRaw[]>(
      ["rechargeSubscriptions"],
      (oldData) =>
        oldData?.map((sub) => (sub.id === data.id ? { ...data } : sub))
    );
  }
};

const updateSubscriptionsQueryData = (
  queryClient: QueryClient,
  data: RechargeItemRaw[]
) => {
  queryClient.setQueryData<RechargeItemRaw[]>(
    ["rechargeSubscriptions"],
    () => data
  );
};

export function useAddress() {
  const { rechargeItem } = useSelectedItemContext().boxItem;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (addressId: number) => {
      return updateAddress({
        orderId: rechargeItem.id,
        addressId,
      });
    },
    onSuccess: (data) => {
      track({ name: "Portal Update Address" });
      updateSubscriptionQueryData(queryClient, data);
    },
    onError: (error) => handleError(error),
  });
}

export function useQuantity() {
  const { rechargeItem, variant } = useSelectedItemContext().boxItem;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (quantity: number) => {
      if (
        rechargeItem.status === "ONETIME" ||
        !variant.subscription ||
        !rechargeItem.cadence
      ) {
        return updateOneTimeProduct({
          orderId: rechargeItem.id,
          shopifyVariantId: variant.id,
          quantity,
        });
      }

      const variantId = variantSelect.subscriptionVariantId(variant);
      return updateSubscription({
        orderId: rechargeItem.id,
        shopifyVariantId: variantId,
        quantity,
      });
    },
    onSuccess: (data) => {
      track({
        name: "Portal Adjust Quantity",
        item: data,
        previousQuantity: rechargeItem.quantity,
      });
      updateSubscriptionQueryData(queryClient, data);
    },
    onError: (error) => handleError(error),
  });
}

export function useVariant() {
  const { rechargeItem } = useSelectedItemContext().boxItem;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (variables: { variantId: number; quantity: number }) => {
      return updateSubscription({
        orderId: rechargeItem.id,
        shopifyVariantId: variables.variantId,
        quantity: variables.quantity,
      });
    },
    onSuccess: (data) => {
      track({
        name: "Portal Adjust Quantity",
        item: data,
        previousQuantity: rechargeItem.quantity,
      });
      updateSubscriptionQueryData(queryClient, data);
    },
    onError: (error) => handleError(error),
  });
}

export function useChargeDate() {
  const { rechargeItem } = useSelectedItemContext().boxItem;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (newChargeDate: Date) => {
      if (rechargeItem.status === "ONETIME") {
        return updateOneTimeProduct({
          orderId: rechargeItem.id,
          nextChargeDate: newChargeDate,
        });
      }
      return updateNextChargeDate({
        orderId: rechargeItem.id,
        nextChargeDate: newChargeDate,
      });
    },
    onSuccess: (data) => {
      track({
        name: "Portal Update Ship Date",
        item: data,
        previousDate: rechargeItem.nextChargeDate,
      });
      updateSubscriptionQueryData(queryClient, data);
    },
    onError: (error) => handleError(error),
  });
}

export function useProcessCharge() {
  return useMutation({
    mutationFn: async (chargeId: number) => {
      return processCharge({
        chargeId,
      });
    },
    onSuccess: (data) => {
      const { line_items } = data;
      line_items.length &&
        track({
          name: "Portal Order Now",
          items: line_items,
        });
    },
    onError: (error) => handleError(error),
  });
}

export function useSkipCharge() {
  return useMutation({
    mutationFn: async (data: {
      chargeId: number;
      subscriptionIds: number[];
    }) => {
      const { chargeId, subscriptionIds } = data;
      return skipCharge({
        chargeId,
        subscriptionIds,
      });
    },
    onSuccess: (data) => data,
    onError: (error) => handleError(error),
  });
}

export function useSkipSubscription() {
  return useMutation({
    mutationFn: async (subscriptionId: number) => {
      return skipSubscription({
        subscriptionId,
      });
    },
    onSuccess: (data) => {},
    onError: (error) => handleError(error),
  });
}

export function useFetchSubscriptions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => fetchSubscriptions(),
    onSuccess: (data) => {
      updateSubscriptionsQueryData(queryClient, data);
    },
    onError: (error) => handleError(error),
  });
}

export function useUpdateBulkSubscriptions() {
  return useMutation({
    mutationFn: async (data: {
      addressdId: string | number;
      subscriptions: {
        subscriptionId: number;
        nextChargeDate: Date;
      }[];
    }) => updateBulkSubscriptions(data.addressdId, data.subscriptions),
    onSuccess: (data) => data,
    onError: (error) => handleError(error),
  });
}

export function useSubscriptionInterval() {
  const { rechargeItem, variant } = useSelectedItemContext().boxItem;
  if (
    rechargeItem.status === "ONETIME" ||
    !variant.subscription ||
    !rechargeItem.cadence
  ) {
    throw new Error(
      "Cannot update subscription interval on non-subscription item"
    );
  }
  const cadence = rechargeItem.cadence;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (intervalUpdate: Cadence | number) => {
      let subscriptionProperties: Cadence =
        typeof intervalUpdate === "number"
          ? {
              ...cadence,
              frequency: intervalUpdate,
            }
          : intervalUpdate;
      return updateSubscription({
        orderId: rechargeItem.id,
        cadence: subscriptionProperties,
      });
    },
    onSuccess: (data) => {
      track({
        name: "Portal Update Subscription Cadence",
        item: data,
        previousCadence: cadence,
      });
      updateSubscriptionQueryData(queryClient, data);
    },
    onError: (error) => handleError(error),
  });
}

export type UpdateBoxItemParams = {
  product: DroppsProduct;
  shopifyVariantId?: number;
  isSubscription: boolean;
  quantity?: number;
  cadence?: Cadence;
  discountCode?: string;
  applyAutomaticDiscount?: boolean;
};

export function useItem() {
  const order = useSelectedItemContext().boxItem;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (item: UpdateBoxItemParams) => {
      if (order.rechargeItem.status === "ONETIME") {
        return updateOneTimeProduct({
          ...item,
          nextChargeDate: order?.rechargeItem?.nextChargeDate,
          orderId: order.rechargeItem.id,
        });
      }

      return updateSubscription({
        ...item,
        orderId: order.rechargeItem.id,
      });
    },
    onSuccess: (data) => {
      track({
        name: "Portal Swap Item",
        item: data,
        prevItem: order.rechargeItem,
      });
      updateSubscriptionQueryData(queryClient, data);
    },
    onError: (error) => handleError(error),
  });
}

export function useCancelItem() {
  const { rechargeItem } = useSelectedItemContext().boxItem;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (rechargeItem.status !== "ONETIME") {
        return cancelSubscription(rechargeItem.id);
      } else {
        return cancelOnetime(rechargeItem.id);
      }
    },
    onSuccess: (data) => {
      track({ name: "Portal Cancel Order", item: rechargeItem });
      queryClient.setQueryData<RechargeItemRaw[]>(
        ["rechargeSubscriptions"],
        (oldData) =>
          oldData?.map((sub) =>
            sub.id === rechargeItem.id
              ? data ?? {
                  ...sub,
                  status: "CANCELLED",
                }
              : sub
          )
      );
    },
  });
}

function useSelectedItemContext() {
  const context = React.useContext(SelectedItemContext);
  if (context === undefined) {
    throw new Error("context must be used within a valid provider");
  }
  return context;
}

export { SelectedItemProvider, useSelectedItemContext };
